import { useEffect, useState } from "react";
import { Coins, Sparkles, Check, Info } from "lucide-react";
import { fetchCoinPackages } from "@/lib/api";
import { startCheckout, usePaymentReturn } from "@/lib/checkout";
import { useAuth } from "@/contexts/AuthContext";
import Footer from "@/components/Footer";
import CashoutPanel from "@/components/CashoutPanel";
import { COINS } from "@/constants/testIds";

export default function CoinsPage() {
  const { user, refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    fetchCoinPackages().then((d) => setPackages(d.packages));
  }, []);

  const buy = async (pkgId) => {
    setBusy(pkgId);
    await startCheckout(`jx_coins_${pkgId}`);
    setBusy(null);
  };
  usePaymentReturn(refresh);

  return (
    <div data-testid={COINS.root}>
      <div className="max-w-6xl mx-auto px-6 pt-10">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FFE300] mb-3">// power the creators</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading font-black text-5xl sm:text-6xl uppercase tracking-tighter">Coin Store</h1>
            <p className="text-white/60 mt-2 max-w-xl">Coins power tips to developers. Every coin is a high-five for the makers behind your favorite apps.</p>
          </div>
          <div data-testid={COINS.balance} className="glass rounded-2xl px-5 py-3">
            <div className="mono text-[10px] uppercase tracking-widest text-white/50">Your balance</div>
            <div className="flex items-center gap-2 mt-1">
              <Coins className="w-5 h-5 text-[#FFE300]" />
              <span className="font-heading font-black text-3xl">{user?.coins ?? 0}</span>
              <span className="mono text-xs text-white/40 ml-1">coins</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {packages.map((p, i) => {
            const total = p.coins + p.bonus;
            const highlight = i === 2;
            return (
              <div key={p.id} className={`glass rounded-2xl p-6 relative transition-[transform,border-color] hover:-translate-y-1 ${highlight ? "border-[#FFE300]/50" : ""}`}>
                {highlight && (
                  <div className="absolute -top-3 left-6 mono text-[10px] uppercase tracking-widest bg-[#FFE300] text-black px-3 py-1 rounded-full font-bold">
                    Best Value
                  </div>
                )}
                <div className="mono text-[10px] uppercase tracking-widest text-white/50">{p.label}</div>
                <div className="flex items-baseline gap-2 mt-3">
                  <Coins className="w-6 h-6 text-[#FFE300]" />
                  <span className="font-heading font-black text-4xl">{total.toLocaleString()}</span>
                </div>
                {p.bonus > 0 && (
                  <div className="mono text-[10px] text-[#39FF14] mt-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> {p.coins} + {p.bonus} bonus
                  </div>
                )}
                <div className="mt-4 font-heading font-black text-xl">${p.price.toFixed(2)}</div>
                <button
                  data-testid={`${COINS.buyBtn}-${p.id}`}
                  onClick={() => buy(p.id)}
                  disabled={busy === p.id}
                  className="mt-4 w-full mono text-xs uppercase tracking-widest bg-[#FFE300] hover:bg-white text-black font-bold py-2.5 rounded-full transition-colors disabled:opacity-50"
                >
                  {busy === p.id ? "Processing…" : "Buy Now"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-10 glass rounded-2xl p-5 flex items-start gap-3">
          <Info className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-bold">Instant coins · pay when you want</div>
            <p className="text-white/60 text-sm mt-1">
              Coins are credited instantly. You can also earn them free by watching optional ads or cash them out any time.
            </p>
          </div>
        </div>

        <div className="mt-10">
          <CashoutPanel />
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { t: "Tip developers", d: "Send coins to creators whose apps you love." },
            { t: "Feature perks", d: "Unlock badges, custom avatars and exclusive playlists (soon)." },
            { t: "No expiry", d: "Your coins never disappear — spend them any time." },
          ].map((f) => (
            <div key={f.t} className="glass rounded-2xl p-5">
              <Check className="w-5 h-5 text-[#39FF14]" />
              <div className="font-heading font-bold mt-2">{f.t}</div>
              <div className="text-white/60 text-sm mt-1">{f.d}</div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
